// TrackPatrollingComponent.jsx
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftCircle, Info, MapPin, RefreshCw, Truck, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/button.js';
import { driverApi } from '../../Driver/driverApi.js';
import { getVehicleById } from '../../Onboarding/vehicleOnboardingApi.js';
import { getProfilePhoto } from '../../OrgManagement/employeeApiService.js';
import { SearchBar } from '../../ui';
import LivePatrolCard from './LivePatrolCard.js';

// Utility function to combine Tailwind CSS classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

/**
 * TrackPatrollingComponent allows users to view active patrols.
 * It functions as a side panel, displaying a list of patrols or a detailed view of a single patrol.
 *
 * @param {Object[]} livePatrols - An array of current live patrol objects to display.
 * @param {Object[]} officeLocations - An array of office location objects to populate site dropdowns.
 * @param {Object[]} employees - An array of employee objects with their status information.
 * @param {function(Object): void} onDispatchNewPatrol - Callback function to dispatch a new patrol.
 * @param {function(): void} onClose - Callback function to close the entire panel.
 * @param {function(string|null): void} onSelectPatrolForMap - Callback to inform parent (Dashboard) about selected patrol for map display.
 * @param {string|null} selectedPatrolIdForMap - The ID of the patrol currently selected for map display (from Dashboard).
 * @param {function(): void} onShowOfficerInfo - Callback to trigger the officer info table.
 * @param {boolean} hideCloseButton - Whether to hide the close button (for dedicated dashboard view).
 * @param {function(): void} onRefresh - Callback function to refresh the live patrols list.
 * @param {function(Object): void} onOfficerClick - Callback when an officer/employee is clicked. Passes {patrolId, userId, officerName}.
 * @param {Object|null} selectedOfficer - Currently selected officer object with patrolId, userId, officerName.
 * @param {Object} qrLookup - Lookup map for QR codes to resolve IDs to names and addresses.
 */
const TrackPatrollingComponent = ({ 
  livePatrols, 
  officeLocations, 
  employees = [], 
  onDispatchNewPatrol, 
  onClose, 
  onSelectPatrolForMap, 
  selectedPatrolIdForMap, 
  onShowOfficerInfo, 
  hideCloseButton = false, 
  onRefresh, 
  onOfficerClick, 
  selectedOfficer,
  qrLookup = {}
}) => {
  // State to manage which patrol is currently expanded in the detailed view within this panel
  const [expandedPatrolId, setExpandedPatrolId] = useState(null);

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // State for patrol officers details
  const [patrolOfficers, setPatrolOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  
  // State for group tabs
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(null); // null = "All Groups", number = group index
  
  // State for group employees
  const [groupEmployees, setGroupEmployees] = useState([]);
  const [loadingGroupEmployees, setLoadingGroupEmployees] = useState(false);
  
  // State for vehicle details (for re-rendering when vehicles are fetched)
  const [vehicleDetailsCache, setVehicleDetailsCache] = useState(new Map()); // Map<vehicleId, vehicleDetails>
  
  // Ref for vehicle cache to avoid dependency issues
  const vehicleDetailsCacheRef = useRef(new Map()); // Map<vehicleId, vehicleDetails>
  
  // Cache for employee/user details to avoid repeated API calls
  const employeeDetailsCacheRef = useRef(new Map()); // Map<userId, { user_name, timestamp }>

  // Synchronize internal expandedPatrolId with external selectedPatrolIdForMap
  useEffect(() => {
    setExpandedPatrolId(selectedPatrolIdForMap);
    // Reset selected group when patrol changes
    setSelectedGroupIndex(null);
  }, [selectedPatrolIdForMap]);

  // Fetch patrol officers details when a patrol is expanded
  useEffect(() => {
    const fetchPatrolOfficers = async () => {
      if (!expandedPatrolId) {
        setPatrolOfficers([]);
        return;
      }

      const currentExpandedPatrol = livePatrols.find(patrol => patrol.id === expandedPatrolId);
      if (!currentExpandedPatrol) {
        setPatrolOfficers([]);
        return;
      }

      setLoadingOfficers(true);
      try {
        // Get patrol officers from the patrol object
        if (currentExpandedPatrol.patrol_officers && Array.isArray(currentExpandedPatrol.patrol_officers)) {
          // Map patrol_officers to include employee information with officer names and profile images
          const officersWithEmployeeInfo = await Promise.all(
            currentExpandedPatrol.patrol_officers.map(async (patrolOfficer) => {
              // Find matching employee from employees array
              const employee = employees.find(emp => emp.id === patrolOfficer.user_id);
              
              // Determine name - try employee array first, then cache, then API
              let name = 'Unknown';
              const userId = patrolOfficer.user_id;
              
              if (employee && employee.name) {
                // Use name from employees array if available
                name = employee.name;
              } else if (userId) {
                // Check cache first
                const cachedEmployee = employeeDetailsCacheRef.current.get(userId);
                if (cachedEmployee && cachedEmployee.user_name) {
                  name = cachedEmployee.user_name;
                  console.log(`✅ Using cached employee name for ${userId}: ${name}`);
                } else {
                  // Fetch from API using driverApi.fetchDriverDetails (works for any user)
                  try {
                    console.log(`🔍 Fetching employee details from API for ${userId}...`);
                    const employeeDetails = await driverApi.fetchDriverDetails(userId);
                    name = employeeDetails?.user_name || employeeDetails?.name || userId || 'Unknown';
                    
                    // Cache the result
                    if (employeeDetails && employeeDetails.user_name) {
                      employeeDetailsCacheRef.current.set(userId, {
                        user_name: employeeDetails.user_name,
                        timestamp: Date.now()
                      });
                      console.log(`✅ Fetched and cached employee name for ${userId}: ${name}`);
                    } else {
                      console.warn(`⚠️ Employee details API response missing user_name for ${userId}`);
                      // Fallback to user_id if API doesn't return name
                      name = userId;
                    }
                  } catch (error) {
                    console.error(`❌ Error fetching employee details for ${userId}:`, error);
                    // Fallback to user_id if API call fails
                    name = userId;
                  }
                }
              }
              
              // Try to fetch profile image
              let profileImage = null;
              try {
                if (userId) {
                  profileImage = await getProfilePhoto(userId);
                }
              } catch (photoError) {
                console.warn(`Failed to fetch profile photo for ${name}:`, photoError);
              }
              
              // Use profile image, fallback to employee image, then placeholder
              const imageUrl = profileImage || 
                             employee?.image || 
                             `https://placehold.co/48x48/aabbcc/ffffff?text=${name.charAt(0).toUpperCase()}`;
              
              // Use employee status from the employees array
              const officerStatus = employee?.status || 'Unknown';
              
              return {
                user_id: patrolOfficer.user_id,
                employee_id: patrolOfficer.employee_id,
                name: name,
                image: imageUrl,
                role: employee?.role || 'Officer',
                status: officerStatus,
              };
            })
          );
          
          setPatrolOfficers(officersWithEmployeeInfo);
        } else {
          setPatrolOfficers([]);
        }
      } catch (error) {
        console.error('Error fetching patrol officers details:', error);
        setPatrolOfficers([]);
      } finally {
        setLoadingOfficers(false);
      }
    };

    fetchPatrolOfficers();
  }, [expandedPatrolId, livePatrols, employees]);

  // Handle search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate search suggestions based on Start/End QR codes and names
    if (value.length > 0) {
      const suggestions = new Set();
      livePatrols.forEach(patrol => {
        // Search by QR names (preferred)
        if (patrol.start_qr_name && patrol.start_qr_name.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(patrol.start_qr_name);
        }
        if (patrol.end_qr_name && patrol.end_qr_name.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(patrol.end_qr_name);
        }
        // Fallback to QR IDs
        if (patrol.start_qr && patrol.start_qr.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(patrol.start_qr);
        }
        if (patrol.end_qr && patrol.end_qr.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(patrol.end_qr);
        }
      });
      setSearchSuggestions(Array.from(suggestions));
    } else {
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
    setSearchTerm(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
  };

  // Filter patrols based on search term (Start/End QR names and IDs)
  const filteredPatrols = useMemo(() => {
    if (!searchTerm.trim()) {
      return livePatrols;
    }
    const searchLower = searchTerm.toLowerCase();
    return livePatrols.filter(patrol => 
      (patrol.start_qr_name && patrol.start_qr_name.toLowerCase().includes(searchLower)) ||
      (patrol.end_qr_name && patrol.end_qr_name.toLowerCase().includes(searchLower)) ||
      (patrol.start_qr && patrol.start_qr.toLowerCase().includes(searchLower)) ||
      (patrol.end_qr && patrol.end_qr.toLowerCase().includes(searchLower))
    );
  }, [livePatrols, searchTerm]);

  // Helper function to get status color based on officer status
  const getStatusColor = (status) => {
    if (!status) {
      return { dot: 'bg-gray-500', text: 'text-gray-600' };
    }
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'on duty') {
      return { dot: 'bg-green-700', text: 'text-green-700' };
    } else if (statusLower === 'duty not started') {
      return { dot: 'bg-yellow-500', text: 'text-yellow-600' };
    } else if (statusLower === 'duty ended') {
      return { dot: 'bg-gray-500', text: 'text-gray-600' };
    } else {
      return { dot: 'bg-gray-500', text: 'text-gray-600' };
    }
  };

  // Helper function to format time range from timestamps
  const formatTimeRange = (startTimestamp, endTimestamp) => {
    try {
      if (!startTimestamp || !endTimestamp) {
        return 'Time not available';
      }
      
      // Parse ISO timestamp strings
      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);
      
      // Extract hours and minutes, pad with zero if needed
      const startHours = String(startDate.getHours()).padStart(2, '0');
      const startMinutes = String(startDate.getMinutes()).padStart(2, '0');
      const endHours = String(endDate.getHours()).padStart(2, '0');
      const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
      
      return `${startHours}:${startMinutes} to ${endHours}:${endMinutes}`;
    } catch (error) {
      console.error('Error formatting time range:', error);
      return 'Time not available';
    }
  };

  // Function to handle selecting a patrol for detailed view
  const handleSelectPatrol = (patrolId) => {
    const newId = expandedPatrolId === patrolId ? null : patrolId;
    setExpandedPatrolId(newId);
    onSelectPatrolForMap(newId); // Inform Dashboard to update map
  };

  // Get current expanded patrol
  const currentExpandedPatrol = livePatrols.find(patrol => patrol.id === expandedPatrolId);

  // Get groups from patrol_details
  const getGroups = useMemo(() => {
    if (!currentExpandedPatrol || !currentExpandedPatrol.patrol_details) {
      return [];
    }
    return currentExpandedPatrol.patrol_details || [];
  }, [currentExpandedPatrol]);

  // Fetch vehicle details
  const fetchVehicleDetails = async (vehicleId) => {
    if (!vehicleId) return null;
    
    // Check cache first
    if (vehicleDetailsCache.has(vehicleId)) {
      return vehicleDetailsCache.get(vehicleId);
    }
    
    try {
      const vehicleDetails = await getVehicleById(vehicleId);
      setVehicleDetailsCache(prev => new Map(prev).set(vehicleId, vehicleDetails));
      return vehicleDetails;
    } catch (error) {
      console.error(`Error fetching vehicle details for ${vehicleId}:`, error);
      return null;
    }
  };

  // Fetch vehicle details for all groups
  useEffect(() => {
    const fetchAllVehicleDetails = async () => {
      if (!currentExpandedPatrol || !currentExpandedPatrol.patrol_details) {
        return;
      }
      
      const vehicleIds = new Set();
      currentExpandedPatrol.patrol_details.forEach(group => {
        if (group.vehicle_id) {
          vehicleIds.add(group.vehicle_id);
        }
      });
      
      // Fetch vehicle details for all vehicles that aren't cached
      const fetchPromises = Array.from(vehicleIds)
        .filter(vehicleId => !vehicleDetailsCacheRef.current.has(vehicleId))
        .map(async (vehicleId) => {
          try {
            const vehicleDetails = await getVehicleById(vehicleId);
            vehicleDetailsCacheRef.current.set(vehicleId, vehicleDetails);
            // Update state to trigger re-render
            setVehicleDetailsCache(new Map(vehicleDetailsCacheRef.current));
          } catch (error) {
            console.error(`Error fetching vehicle details for ${vehicleId}:`, error);
          }
        });
      
      await Promise.all(fetchPromises);
    };
    
    if (currentExpandedPatrol && currentExpandedPatrol.patrol_details) {
      fetchAllVehicleDetails();
    }
  }, [currentExpandedPatrol]);

  // Fetch employees for selected group
  useEffect(() => {
    const fetchGroupEmployees = async () => {
      if (selectedGroupIndex === null) {
        // Show all employees
        setGroupEmployees(patrolOfficers);
        return;
      }
      
      if (!currentExpandedPatrol || !currentExpandedPatrol.patrol_details) {
        setGroupEmployees([]);
        return;
      }
      
      const group = currentExpandedPatrol.patrol_details[selectedGroupIndex];
      if (!group || !group.user_ids || !Array.isArray(group.user_ids)) {
        setGroupEmployees([]);
        return;
      }
      
      setLoadingGroupEmployees(true);
      try {
        // Map user_ids to employee objects
        const employeesList = await Promise.all(
          group.user_ids.map(async (userId) => {
            const employee = employees.find(emp => emp.id === userId);
            
            // Get name
            let name = 'Unknown';
            if (employee && employee.name) {
              name = employee.name;
            } else if (userId) {
              const cachedEmployee = employeeDetailsCacheRef.current.get(userId);
              if (cachedEmployee && cachedEmployee.user_name) {
                name = cachedEmployee.user_name;
              } else {
                try {
                  const employeeDetails = await driverApi.fetchDriverDetails(userId);
                  name = employeeDetails?.user_name || employeeDetails?.name || userId || 'Unknown';
                  if (employeeDetails && employeeDetails.user_name) {
                    employeeDetailsCacheRef.current.set(userId, {
                      user_name: employeeDetails.user_name,
                      timestamp: Date.now()
                    });
                  }
                } catch (error) {
                  console.error(`Error fetching employee details for ${userId}:`, error);
                  name = userId;
                }
              }
            }
            
            // Get profile image
            let profileImage = null;
            try {
              if (userId) {
                profileImage = await getProfilePhoto(userId);
              }
            } catch (photoError) {
              console.warn(`Failed to fetch profile photo for ${name}:`, photoError);
            }
            
            const imageUrl = profileImage || 
                           employee?.image || 
                           `https://placehold.co/48x48/aabbcc/ffffff?text=${name.charAt(0).toUpperCase()}`;
            
            return {
              user_id: userId,
              name: name,
              image: imageUrl,
              role: employee?.role || 'Officer',
              status: employee?.status || 'Unknown',
            };
          })
        );
        
        setGroupEmployees(employeesList);
      } catch (error) {
        console.error('Error fetching group employees:', error);
        setGroupEmployees([]);
      } finally {
        setLoadingGroupEmployees(false);
      }
    };
    
    fetchGroupEmployees();
  }, [selectedGroupIndex, currentExpandedPatrol, employees, patrolOfficers]);

  return (
    <motion.div
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '100%' }}
      transition={{ duration: 0.3 }}
      className="w-full md:w-96 self-stretch relative bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg overflow-hidden"
      style={{ height: '100%', minHeight: '300px' }}
    >
      {/* Header Section */}
      <div className="w-full h-20 left-0 top-0 absolute bg-neutral-50/40 rounded-xl shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] flex items-center justify-between px-4 md:px-6">
        {expandedPatrolId ? (
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleSelectPatrol(null)}
              className="w-8 h-8 flex items-center justify-center bg-neutral-50/50 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px]"
              aria-label="Back to all patrols"
            >
              <ArrowLeftCircle className="w-5 h-5 text-black" />
            </button>
            <div className="text-center text-black text-base md:text-xl font-bold font-['Figtree'] leading-normal">Patrol Details</div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="text-black text-base md:text-xl font-bold font-['Figtree'] leading-normal">Track Live Patrols</div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="w-8 h-8 flex items-center justify-center bg-blue-500/10 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px] hover:bg-blue-500/20 transition-colors"
              aria-label="Refresh live patrols"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </button>
          )}
          {!hideCloseButton && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-neutral-50/50 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px]"
              aria-label="Close track patrols panel"
            >
              <XCircle className="w-5 h-5 text-black" />
            </button>
          )}
        </div>
      </div>

      {/* Content Area - Conditional Rendering */}
      <div className="w-full left-0 md:left-[16px] top-[96px] absolute flex flex-col justify-start items-start gap-6 overflow-y-auto custom-scrollbar px-4 md:px-0 md:pr-6" style={{ height: 'calc(100% - 128px)' }}>
        {expandedPatrolId && currentExpandedPatrol ? (
          // Display single expanded patrol details using LivePatrolCard
          <>
            <LivePatrolCard
              patrol={currentExpandedPatrol}
              onClick={() => {}}
              onShowOfficerInfo={onShowOfficerInfo}
              isSelected={true}
              qrLookup={qrLookup}
            />

            

            {/* Groups Section with Tabs */}
            {currentExpandedPatrol && currentExpandedPatrol.patrol_details && currentExpandedPatrol.patrol_details.length > 0 && (
              <div className="self-stretch">

                {/* Officer Section - Header Only (cards removed, shown in Groups section) */}
            <div className="self-stretch">
              <div className="w-full h-10 inline-flex justify-between items-center mb-2">
                <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-snug">Officers</div>
                <div className="flex justify-start items-center gap-2.5">
                  <button
                    onClick={() => {
                      // Pass the current patrol's patrol_officers to display all officers with vehicle, material, notes
                      // Use patrol_details structure to preserve group information
                      if (currentExpandedPatrol && currentExpandedPatrol.patrol_details && Array.isArray(currentExpandedPatrol.patrol_details)) {
                        // Build officer data from patrol_details to preserve group structure
                        const officerData = [];
                        let serialNumber = 1;
                        
                        // Iterate through each group in patrol_details
                        currentExpandedPatrol.patrol_details.forEach((patrolDetail, groupIndex) => {
                          const userIds = patrolDetail.user_ids || [];
                          const vehicleId = patrolDetail.vehicle_id || 'N/A';
                          const material = patrolDetail.material || 'N/A';
                          const notes = patrolDetail.notes || patrolDetail.note || 'N/A';
                          
                          // For each user in this group, create an officer entry
                          userIds.forEach((userId) => {
                            // Find matching officer info from patrolOfficers or employees
                            const officer = patrolOfficers.find(po => po.user_id === userId) ||
                                          employees.find(emp => emp.id === userId);
                            
                            officerData.push({
                              serialNumber: serialNumber++,
                              userId: userId || 'N/A',
                              officerName: officer?.name || 'Unknown Officer',
                              patrolId: currentExpandedPatrol.patrol_id || currentExpandedPatrol.id,
                              siteId: currentExpandedPatrol.site_id,
                              status: officer?.status || currentExpandedPatrol.status || 'in progress',
                              siteName: currentExpandedPatrol.siteName || 'N/A',
                              vehicleId: vehicleId,
                              material: material,
                              notes: notes,
                              groupIndex: groupIndex, // Preserve the group index from patrol_details
                            });
                          });
                        });
                        
                        if (officerData.length > 0) {
                          onShowOfficerInfo(officerData);
                        }
                      } else if (patrolOfficers && patrolOfficers.length > 0) {
                        // Fallback: Transform patrolOfficers into table format
                        const officerData = patrolOfficers.map((officer, index) => {
                          // Find matching patrol_officer from currentExpandedPatrol to get vehicle, material, notes
                          const patrolOfficerDetails = currentExpandedPatrol?.patrol_officers?.find(
                            po => po.user_id === officer.user_id
                          );
                          
                          // Try to find group index from patrol_details
                          let groupIndex = null;
                          if (currentExpandedPatrol?.patrol_details) {
                            const detailIndex = currentExpandedPatrol.patrol_details.findIndex(detail =>
                              detail.user_ids && Array.isArray(detail.user_ids) && detail.user_ids.includes(officer.user_id)
                            );
                            if (detailIndex !== -1) {
                              groupIndex = detailIndex;
                            }
                          }
                          
                          return {
                            serialNumber: index + 1,
                            userId: officer.user_id || 'N/A',
                            officerName: officer.name || 'N/A',
                            patrolId: currentExpandedPatrol.patrol_id || currentExpandedPatrol.id,
                            siteId: currentExpandedPatrol.site_id,
                            status: officer.status || currentExpandedPatrol.status || 'in progress',
                            siteName: currentExpandedPatrol.siteName || 'N/A',
                            vehicleId: patrolOfficerDetails?.vehicle_id || 'N/A',
                            material: patrolOfficerDetails?.material || 'N/A',
                            notes: patrolOfficerDetails?.notes || 'N/A',
                            groupIndex: groupIndex,
                          };
                        });
                        onShowOfficerInfo(officerData);
                      } else if (currentExpandedPatrol && currentExpandedPatrol.patrol_officers) {
                        // Fallback to raw patrol_officers if patrolOfficers state is not available
                        const officerData = currentExpandedPatrol.patrol_officers.map((officer, index) => ({
                          serialNumber: index + 1,
                          userId: officer.user_id || 'N/A',
                          officerName: officer.name || officer.officer?.name || 'N/A',
                          patrolId: currentExpandedPatrol.patrol_id || currentExpandedPatrol.id,
                          siteId: currentExpandedPatrol.site_id,
                          status: currentExpandedPatrol.status || 'in progress',
                          siteName: currentExpandedPatrol.siteName || 'N/A',
                          vehicleId: officer.vehicle_id || 'N/A',
                          material: officer.material || 'N/A',
                          notes: officer.notes || 'N/A',
                          groupIndex: null, // Cannot determine group without patrol_details
                        }));
                        onShowOfficerInfo(officerData);
                      }
                    }}
                    className="p-2 bg-neutral-50/50 rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 flex justify-start items-center gap-2.5 overflow-hidden"
                    title="View Patrol Information"
                  >
                    <Info className="w-5 h-5 text-black" />
                  </button>
                </div>
              </div>
            </div>
                
                {/* Group Tabs */}
                <div className="w-full flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setSelectedGroupIndex(null)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedGroupIndex === null
                        ? 'bg-blue-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <span 
                      className={selectedGroupIndex === null ? 'group-tab-text-selected' : ''}
                      style={selectedGroupIndex === null ? { 
                        color: '#ffffff',
                        WebkitTextFillColor: '#ffffff',
                        WebkitTextStroke: 'none'
                      } : undefined}
                    >
                      All Groups
                    </span>
                  </button>
                  {getGroups.map((group, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedGroupIndex(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedGroupIndex === index
                          ? 'bg-blue-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <span 
                        className={selectedGroupIndex === index ? 'group-tab-text-selected' : ''}
                        style={selectedGroupIndex === index ? { 
                          color: '#ffffff',
                          WebkitTextFillColor: '#ffffff',
                          WebkitTextStroke: 'none'
                        } : undefined}
                      >
                        Group {index + 1}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Group Content - Vehicle and Employees */}
                <div className="w-full flex flex-col justify-start items-center gap-3 overflow-y-auto" style={{ minHeight: '400px', maxHeight: '100%' }}>
                  {loadingGroupEmployees ? (
                    <div className="self-stretch pb-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed">
                      Loading group details...
                    </div>
                  ) : selectedGroupIndex === null ? (
                    // Show all groups
                    <>
                      {getGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="self-stretch flex flex-col gap-3">
                          {/* Group Header */}
                          <div className="text-black text-base font-semibold font-['Figtree']">
                            Group {groupIndex + 1}
                          </div>
                          
                          {/* Vehicle Card */}
                          {group.vehicle_id && (
                            <div className="self-stretch px-3 py-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex items-center gap-3">
                              <Truck className="w-8 h-8 text-blue-600" />
                              <div className="flex-1">
                                <div className="text-black text-base font-medium font-['Open_Sans']">
                                  {vehicleDetailsCache.get(group.vehicle_id)?.registration_no || 
                                   vehicleDetailsCache.get(group.vehicle_id)?.model_name || 
                                   group.vehicle_id}
                                </div>
                                {vehicleDetailsCache.get(group.vehicle_id)?.model_name && (
                                  <div className="text-gray-600 text-sm font-['Open_Sans']">
                                    {vehicleDetailsCache.get(group.vehicle_id).model_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Employees in this group */}
                          {group.user_ids && Array.isArray(group.user_ids) && group.user_ids.length > 0 && (
                            <div className="self-stretch flex flex-col gap-2">
                              {group.user_ids.map((userId) => {
                                const employee = patrolOfficers.find(po => po.user_id === userId) || 
                                               employees.find(emp => emp.id === userId);
                                if (!employee) return null;
                                
                                return (
                                  <div key={userId} className="self-stretch px-3 py-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex items-center gap-3">
                                    <img 
                                      className="w-12 h-12 relative rounded-lg object-cover" 
                                      src={employee.image || `https://placehold.co/48x48/aabbcc/ffffff?text=${(employee.name || userId).charAt(0).toUpperCase()}`}
                                      alt={employee.name}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        const fallbackText = (employee.name || userId)?.charAt(0).toUpperCase() || '?';
                                        e.target.src = `https://placehold.co/48x48/aabbcc/ffffff?text=${fallbackText}`;
                                      }}
                                    />
                                    <div className="flex-1">
                                      <div className="text-black text-base font-normal font-['Open_Sans']">
                                        {employee.name || userId}
                                      </div>
                                      <div className="text-gray-600 text-sm font-['Open_Sans']">
                                        {employee.role || 'Officer'}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Material and Notes */}
                          {(group.material || group.notes) && (
                            <div className="self-stretch px-3 py-2 bg-gray-50 rounded-lg">
                              {group.material && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-semibold">Material:</span> {group.material}
                                </div>
                              )}
                              {group.notes && (
                                <div className="text-sm text-gray-700 mt-1">
                                  <span className="font-semibold">Notes:</span> {group.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    // Show selected group
                    <>
                      {(() => {
                        const selectedGroup = getGroups[selectedGroupIndex];
                        if (!selectedGroup) return null;
                        
                        return (
                          <>
                            {/* Vehicle Card */}
                            {selectedGroup.vehicle_id && (
                              <div className="self-stretch px-3 py-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex items-center gap-3">
                                <Truck className="w-8 h-8 text-blue-600" />
                                <div className="flex-1">
                                  <div className="text-black text-base font-medium font-['Open_Sans']">
                                    {vehicleDetailsCache.get(selectedGroup.vehicle_id)?.registration_no || 
                                     vehicleDetailsCache.get(selectedGroup.vehicle_id)?.model_name || 
                                     selectedGroup.vehicle_id}
                                  </div>
                                  {vehicleDetailsCache.get(selectedGroup.vehicle_id)?.model_name && (
                                    <div className="text-gray-600 text-sm font-['Open_Sans']">
                                      {vehicleDetailsCache.get(selectedGroup.vehicle_id).model_name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Employees in selected group */}
                            {loadingGroupEmployees ? (
                              <div className="self-stretch pb-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed">
                                Loading employees...
                              </div>
                            ) : groupEmployees.length > 0 ? (
                              groupEmployees.map((employee) => (
                                <div key={employee.user_id} className="self-stretch px-3 py-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex items-center gap-3">
                                  <img 
                                    className="w-12 h-12 relative rounded-lg object-cover" 
                                    src={employee.image}
                                    alt={employee.name}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      const fallbackText = employee.name?.charAt(0).toUpperCase() || employee.user_id?.charAt(0).toUpperCase() || '?';
                                      e.target.src = `https://placehold.co/48x48/aabbcc/ffffff?text=${fallbackText}`;
                                    }}
                                  />
                                  <div className="flex-1">
                                    <div className="text-black text-base font-normal font-['Open_Sans']">
                                      {employee.name}
                                    </div>
                                    <div className="text-gray-600 text-sm font-['Open_Sans']">
                                      {employee.role}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {(() => {
                                      const statusColors = getStatusColor(employee.status);
                                      return (
                                        <>
                                          <div className={combineClasses("w-3 h-3 rounded-full", statusColors.dot)} />
                                          <div className={combineClasses("text-sm font-normal font-['Open_Sans'] whitespace-nowrap", statusColors.text)}>
                                            {employee.status}
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="self-stretch pb-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed text-gray-500">
                                No employees in this group
                              </div>
                            )}
                            
                            {/* Material and Notes */}
                            {(selectedGroup.material || selectedGroup.notes) && (
                              <div className="self-stretch px-3 py-2 bg-gray-50 rounded-lg">
                                {selectedGroup.material && (
                                  <div className="text-sm text-gray-700">
                                    <span className="font-semibold">Material:</span> {selectedGroup.material}
                                  </div>
                                )}
                                {selectedGroup.notes && (
                                  <div className="text-sm text-gray-700 mt-1">
                                    <span className="font-semibold">Notes:</span> {selectedGroup.notes}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          // Display list of all live patrols
          <>
            {/* Search Bar Section */}
            <div className="mb-4 w-full">
              <SearchBar
                placeholder="Search by Start/End QR..."
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
                suggestions={searchSuggestions}
                onSuggestionClick={handleSuggestionClick}
                className="w-full"
              />
            </div>
            {filteredPatrols.length > 0 ? (
              <div className="space-y-4 w-full">
                <AnimatePresence>
                  {filteredPatrols.map(patrol => (
                    <motion.div
                      key={patrol.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.5 }}
                      className="self-stretch p-3 rounded-xl outline outline-1 outline-offset-[-1px] outline-white flex flex-col justify-start items-start gap-2.5 cursor-pointer hover:opacity-90 transition-opacity"
                      style={{
                        background: 'radial-gradient(152.09% 184.58% at 136.24% -51.30%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                      }}
                      onClick={() => handleSelectPatrol(patrol.id)}
                    >
                      <div className="self-stretch inline-flex justify-between items-center">
                        <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-snug">
                          {patrol.patrol_name || formatTimeRange(patrol.patrol_start_timestamp, patrol.patrol_end_timestamp)}
                        </div>
                        <div className="p-1 flex justify-start items-center gap-2 overflow-hidden">
                          <MapPin className="w-6 h-6 text-black" />
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectPatrol(patrol.id);
                            }}
                            className={combineClasses("px-3 py-1 text-sm rounded-full !text-white", selectedPatrolIdForMap === patrol.id ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-600 hover:bg-blue-700")}
                          >
                            {selectedPatrolIdForMap === patrol.id ? "Tracking" : "Track Patrol"}
                          </Button>
                        </div>
                      </div>
                      <div className="self-stretch justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                        {formatTimeRange(patrol.patrol_start_timestamp, patrol.patrol_end_timestamp)}
                      </div>
                      {patrol.siteName && patrol.siteName !== 'Unknown' && (
                        <div className="self-stretch justify-start text-black text-base font-normal font-['Open_Sans'] leading-relaxed">
                          {patrol.siteName}
                        </div>
                      )}
                      {patrol.supervisorName && (
                        <div className="self-stretch justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                          Supervisor: {patrol.supervisorName}
                        </div>
                      )}
                      {/* QR Code Information */}
                      <div className="self-stretch flex flex-col gap-1 mt-2">
                        {(patrol.start_qr_name || patrol.start_qr) && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">Start QR:</span>
                              <span className="text-xs text-gray-600 bg-green-100 px-2 py-1 rounded">
                                {patrol.start_qr_name || patrol.start_qr}
                              </span>
                            </div>
                            {patrol.start_qr_address && (
                              <div className="text-xs text-gray-500 ml-16">{patrol.start_qr_address}</div>
                            )}
                          </div>
                        )}
                        {(patrol.end_qr_name || patrol.end_qr) && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">End QR:</span>
                              <span className="text-xs text-gray-600 bg-red-100 px-2 py-1 rounded">
                                {patrol.end_qr_name || patrol.end_qr}
                              </span>
                            </div>
                            {patrol.end_qr_address && (
                              <div className="text-xs text-gray-500 ml-16">{patrol.end_qr_address}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="self-stretch p-4 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed text-gray-500">
                {searchTerm ? 'No patrols found matching your search.' : 'No active patrols at this time.'}
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default TrackPatrollingComponent;

